import { onScopeDispose, ref, watch } from 'vue';
import type { Ref } from 'vue';
import TablesService from '@/api/services/tables.service';
import type { IReservationGuideScope } from './reservation-guide-context';

/** Requests in flight at once (D13). */
const CONCURRENCY = 3;

export interface IReservationTablesCreateProgress {
  done: number;
  total: number;
}

export interface IReservationTablesCreate {
  /** Non-null while a run is in progress. */
  progress: Ref<IReservationTablesCreateProgress | null>;
  /** The last run stopped on a failure; «Reintentar» passes the names still missing. */
  failed: Ref<boolean>;
  create(names: readonly string[], capacity: number): Promise<boolean>;
}

/**
 * Zero-table branches: creates the given tables through `POST /tables`, three
 * at a time; any failure stops the run. The caller recomputes the missing
 * names from the reloaded bundle before retrying (no server-message sniffing).
 */
export function useReservationTablesCreate(
  scope: () => IReservationGuideScope | null
): IReservationTablesCreate {
  const progress = ref<IReservationTablesCreateProgress | null>(null);
  const failed = ref(false);
  let alive = true;
  let running: { key: string; promise: Promise<boolean> } | null = null;
  const key = () => {
    const current = scope();
    return current ? `${current.restaurantId}:${current.branchId}` : '';
  };

  /** A second call during a run joins that run instead of starting another. */
  function create(names: readonly string[], capacity: number): Promise<boolean> {
    const scopeKey = key();
    // A run started for another branch is not this branch's run.
    if (running && running.key === scopeKey) return running.promise;
    const target = scope();
    if (!target) return Promise.resolve(false);
    if (names.length === 0) {
      failed.value = false;
      return Promise.resolve(true);
    }
    const promise = createAll(target, names, capacity).then((result) => {
      if (running?.promise === promise) running = null;
      return result;
    });
    running = { key: scopeKey, promise };
    return promise;
  }

  async function createAll(
    target: IReservationGuideScope,
    names: readonly string[],
    capacity: number
  ): Promise<boolean> {
    const { restaurantId, branchId } = target;
    const requestKey = key();
    const current = () => alive && key() === requestKey;
    const queue = [...names];
    const total = queue.length;
    let done = 0;
    let stopped = false;
    failed.value = false;
    progress.value = { done, total };
    async function worker() {
      while (!stopped) {
        const name = queue.shift();
        if (name === undefined) return;
        try {
          await TablesService.createTable({
            restaurantId,
            branchId,
            name,
            capacity,
          });
        } catch {
          stopped = true;
          return;
        }
        if (!current()) {
          stopped = true;
          return;
        }
        done++;
        progress.value = { done, total };
      }
    }
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, total) }, () => worker())
    );
    if (!current()) return false;
    progress.value = null;
    failed.value = stopped;
    return !stopped;
  }

  watch(key, () => {
    running = null;
    progress.value = null;
    failed.value = false;
  });
  onScopeDispose(() => {
    alive = false;
  });

  return { progress, failed, create };
}
